// The three destinations of the top navigation and the phone tab bar — ECONIC · Agents ⌄ · My workspace (16 Oct 2026) — plus the
// administration destinations the DT Administrator sees instead (SPEC §3.1; shell-ia §5.1; DESIGN-SYSTEM §10.2). Tasks and Workspaces
// are no longer destinations: their screens stay reachable by link and light My workspace. The nav filters by permission, never by role
// name. WP-SHELL.
import { History, LayoutGrid, MessageSquareText, ShieldCheck, Sparkles, Users, type LucideIcon } from "lucide-react";
import type { Destination } from "@ncp/assistant-sdk/contracts";
import { routeFor } from "../../app/routes";

export interface NavDestination {
  id: Destination | "audit";
  href: string;
  /** i18n key under `assistant:econic.shell.nav.` */
  label: string;
  /** Shorter label for the phone tab bar. */
  tab: string;
  icon: LucideIcon;
  /** Any one of these shows the item. */
  permission: string[];
}

export const PRIMARY: readonly NavDestination[] = [
  // ECONIC: the landing with its large chat box, and every ECONIC conversation (/agent/:threadId).
  { id: "today", href: "/", label: "econic", tab: "econicTab", icon: MessageSquareText, permission: ["assistant.use"] },
  // Agents: a menu of the six agents (the Knowledge Bank among them); on a phone the tab opens the Agents page.
  { id: "intelligence", href: "/intelligence", label: "agents", tab: "agentsTab", icon: Sparkles, permission: ["economics.read", "knowledge.read"] },
  // My workspace: the person's own board, conversations, the department's desks, files and pages.
  { id: "workspaces", href: "/my-workspace", label: "myWorkspace", tab: "myWorkspaceTab", icon: LayoutGrid, permission: ["assistant.use", "pages.use"] },
];

/** The DT Administrator holds no content clearance: Trust Centre · Accounts · Audit log replace the three (DESIGN-SYSTEM §10.2). */
export const ADMIN: readonly NavDestination[] = [
  { id: "trust", href: "/trust", label: "trust", tab: "trust", icon: ShieldCheck, permission: ["trust.read"] },
  { id: "admin", href: "/admin/users", label: "accounts", tab: "accounts", icon: Users, permission: ["platform.accounts.read"] },
  { id: "audit", href: "/admin/audit", label: "audit", tab: "audit", icon: History, permission: ["platform.audit.read"] },
];

/**
 * Which destination a path lights (shell-ia §5.1): a conversation (`/agent/:threadId`) counts as ECONIC; the Knowledge Bank and `/evidence`
 * as Agents; the desks, files, personal pages and the old Tasks screens as My workspace …
 */
export function destinationOf(pathname: string): NavDestination["id"] | null {
  if (pathname === "/admin/audit" || pathname.startsWith("/admin/audit/")) return "audit";
  const def = routeFor(pathname);
  return def?.destination ?? null;
}

/** The destinations a person sees: the three they may open, or the administration set when they hold none of the three. */
export function visibleDestinations(can: (p: string) => boolean): { items: NavDestination[]; admin: boolean } {
  const primary = PRIMARY.filter((d) => d.permission.some(can));
  if (primary.length > 0) return { items: primary, admin: false };
  return { items: ADMIN.filter((d) => d.permission.some(can)), admin: true };
}
